import React, { useState, useRef, useEffect} from "react";
import { useNavigate } from "react-router-dom";
import IngredientSelector from "../components/ingredientSelector";
import { Button, Spin } from "antd";
import { api } from '../api/client';
import FridgeLogo from "../assets/fridge-logo.png";
import "./CustomRecipeWizard.css";
import DogProfileModal, { DogProfile } from "../components/DogProfileModal";
import { BACKEND_URL } from '../config';

const ageOptions = [">1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13", "14", "15", "<15"];

interface Recipe {
  id: number;
  title: string;
  photo_url: string;
  slug: string;
}

// store the age and weight in local storage so that we can make a new call to a new endpoint to get all the portions on the show page 
// when the click on the recipe card - we will make a call to the endpoint - storing the rresult in local storage to use in recipes deailts 
// 

const CustomRecipeWizard: React.FC = () => {
  const [isFridgeOpen, setIsFridgeOpen] = useState(false);
  const [selectedIngredients, setSelectedIngredients] = useState<string[]>([]);
  const [searchedRecipes, setSearchedRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(false);
  // const [error, setError] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [openProfileModal, setOpenProfileModal] = useState(false);
  const [dogProfile, setDogProfile] = useState<DogProfile>({ age: '', weight: '', meals_per_day: 1 });
  const recipeRef = useRef<HTMLDivElement | null>(null);
  // const [dailyPortionGrams, setDailyPortionGrams] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (searchedRecipes.length > 0 && recipeRef.current) {
      recipeRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [searchedRecipes]);


  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      setShowBackToFridgeButton(scrollTop > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const [showBackToFridgeButton, setShowBackToFridgeButton] = useState(false);

  const handleIngredientCheckboxChange = (ingredient: string, checked: boolean) => {
    setSelectedIngredients((prev) =>
      checked ? [...prev, ingredient] : prev.filter((item) => item !== ingredient)
    );
  };

  const handleDoorOpen = () => {
    setIsFridgeOpen(true);
  };

  const handleCloseFridge = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedIngredients([]);
    setIsFridgeOpen(false);
  };

  const handlePrimarySearch = () => {
    setOpenProfileModal(true);
  };

  const handleProfileModalSubmit = (profile: DogProfile) => {
    setDogProfile(profile);
    setOpenProfileModal(false);
    searchRecipes(profile.age, profile.weight);
    setTimeout(() => {
      if (recipeRef.current) {
        recipeRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 400);
  };

  const handleCloseModal = () => {
    setOpenProfileModal(false);
  };

  const openRecipeDetails = (recipe: Recipe) => {
    localStorage.setItem("dogProfile", JSON.stringify({
      age: dogProfile.age,
      weight: dogProfile.weight,
      meals_per_day: dogProfile.meals_per_day,
    }));
    navigate(`/recipes/${recipe.slug}?showPortions=true`);
  };

  // const initialState = {
  //   age: dogProfile.age,
  //   weight: dogProfile.weight
  // }

  const searchRecipes = async (age: string, weight: string) => {
    setLoading(true);
    // setError(null);
    setSearching(true);

    try {
      // If allergies present in dogProfile, split by comma or space
      let allergyTerms: string[] | undefined = undefined;
      if (dogProfile.allergies && dogProfile.allergies.trim()) {
        allergyTerms = dogProfile.allergies
          .split(/[ ,]+/)
          .map(s => s.trim())
          .filter(Boolean);
      }
      const response = await api.post("/recipes/fridge_search", {
        ingredients: selectedIngredients,
        age,
        weight,
        allergies: allergyTerms && allergyTerms.length > 0 ? allergyTerms : undefined,
      });

      if (response.data.length === 0) {
        // setError("No recipes found with the selected ingredients.");
      }

      // setDailyPortionGrams(response.data.recommended_daily_portion_grams);
      setSearchedRecipes(response.data.recipes);
    } catch {
      // setError("An error occurred while searching for recipes.");
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSelectedIngredients([]);
    setSearchedRecipes([]);
    setSearching(false);
    setLoading(false);
  };

  return (
    <div className="main-generator">
      {!isFridgeOpen ? (
        <div className="layout">
          <div className="left-panel">
            <h1 style={{ fontSize: '2.5rem', color: '#b24646', textShadow: '1px 1px 8px #fffbe6' }}>
              Build Your Custom Dog Food Recipe
            </h1>
            <p style={{ fontSize: '1.2rem', color: '#333', marginBottom: '2rem' }}>
              Welcome to the Recipe Wizard! Start by opening the fridge to pick ingredients you have at home. We'll help you create a healthy, delicious meal for your dog.
            </p>
            <ul style={{ textAlign: 'left', margin: '0 auto', maxWidth: '350px', color: '#0e3b43', fontSize: '1.05rem', background: '#f7f7f7', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: '1.2rem' }}>
              <li>🐶 Get recipes tailored to your dog's age & weight</li>
            </ul>
            <button
              className="open-fridge-btn"
              style={{
                marginTop: '2.5rem',
                padding: '1rem 2.2rem',
                background: 'linear-gradient(90deg, #b24646 60%, #d97b66 100%)',
                color: '#fffbe6',
                border: 'none',
                borderRadius: '16px',
                fontWeight: 700,
                fontSize: '1.2rem',
                boxShadow: '0 4px 16px rgba(178,70,70,0.18)',
                cursor: 'pointer',
                transition: 'background 0.2s, box-shadow 0.2s',
              }}
              onClick={handleDoorOpen}
            >
              <span role="img" aria-label="fridge">🧊</span> Open Fridge
            </button>
          </div>
          <div className="right-panel">
            <div className="fridge-container">
              <div
                className="fridge-door"
                style={{ boxShadow: '0 8px 32px rgba(178,70,70,0.18), 0 1px 0 #fffbe6 inset', cursor: 'pointer' }}
                onClick={handleDoorOpen}
                role="button"
                tabIndex={0}
                aria-label="Open fridge"
              >
                <div className="fridge-top-shine"></div>
                <img src={FridgeLogo} alt="Fridge Logo" className="fridge-logo" style={{ filter: 'drop-shadow(0 6px 16px #b24646)' }} />
                <div className="fridge-hinge top-hinge"></div>
                <div className="fridge-hinge bottom-hinge"></div>
                <div className="fridge-explanation">
                  <p style={{ fontSize: '1.15rem', color: '#fffbe6', fontWeight: 500, textShadow: '1px 1px 8px #b24646' }}>
                    Click anywhere on the fridge or the button to open and start your recipe journey!
                  </p>
                </div>
                <button className="fridge-handle" tabIndex={-1}></button>
                <div className="fridge-vertical-shine-right"></div>
              </div>
              <div className="fridge-stand"></div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="layout">
            <div className="left-panel-open">
                <button className="back-button" onClick={handleCloseFridge}>
                  Back
                </button>
                <div className="door-shelf-line" style={{ top: '100px' }}></div>
                <div className="door-shelf-line" style={{ top: '500px' }}></div>
                <div className="door-shelf-line" style={{ top: '600px' }}></div>
                <div className="selected-ingredients">
                  {selectedIngredients.map((ingredient, index) => (
                    <div key={index} className="selected-ingredient-item fancy-float" style={{ animationDelay: `${index * 0.1}s`,}}>
                      {ingredient}
                    </div>
                  ))}
                </div>
                <div className="button-container" style={{ marginTop: 'auto', marginBottom: '2.5rem', display: 'flex', gap: '1.5rem', justifyContent: 'center' }}>
                  <Button
                    type="primary"
                    className={`fridge-button ${selectedIngredients.length === 0 ? "inactive" : ""}`}
                    onClick={handlePrimarySearch}
                    disabled={selectedIngredients.length === 0}
                    loading={loading}
                  >
                    Search Recipes
                  </Button>
                  <Button
                    type="default"
                    className={`fridge-button ${selectedIngredients.length === 0 ? "inactive" : ""}`}
                    onClick={handleClearSearch}
                    disabled={selectedIngredients.length === 0}
                  >
                    Clear Search
                  </Button>
                </div>
            </div>
            <div className="right-panel-open">
              <div>
                <IngredientSelector
                  selectedIngredients={selectedIngredients}
                  onCheckboxChange={handleIngredientCheckboxChange}
                  isLoading={loading}      
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <DogProfileModal
        open={openProfileModal}
        onClose={handleCloseModal}
        onSubmit={handleProfileModalSubmit}
        initialProfile={dogProfile}
        ageOptions={ageOptions}
      />


      {/* Results */}
      {loading && <Spin size="large" className="loading-spinner" />}

      {searching && !loading && searchedRecipes.length === 0 && (
        <div ref={recipeRef} className="error-screen">
          <h2>Sorry, no recipes found</h2>
          <p>Try selecting different ingredients.</p>
        </div>
      )}
      {searchedRecipes.length > 0 && (
        <>
          <div ref={recipeRef} className="recipes-container">
            <h2>Search Results</h2>
            <div className="recipe-grid">
              {searchedRecipes.map((recipe) => (
                <button
                  type="button"
                  key={recipe.id}
                  className="recipe-card"
                  onClick={() => openRecipeDetails(recipe)}
                >
                  <img
                    src={`${BACKEND_URL}${recipe.photo_url}`}
                    alt={recipe.title}
                    className="recipe-image"
                  />
                  <h3 className="recipe-title">{recipe.title}</h3>
                </button>
              ))}
            </div>
          </div>

          {showBackToFridgeButton && (
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="back-to-fridge-button"
            >
            Back to Fridge
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default CustomRecipeWizard;